import {
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDownIcon,
  EllipsisHorizontalIcon,
} from "@heroicons/react/24/outline";
import { Tooltip } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { css } from "styled-components";
import { useAuthStore } from "../../hooks/useAuth";
import { useLogout } from "../../hooks/useLogout";
import {
  MORE_NAV_ITEMS,
  NavItem,
  PRIMARY_NAV_SECTIONS,
  isNavItemActive,
} from "./navItems";

const MORE_OPEN_KEY = "nav_more_open";

const MenuContainer = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const Brand = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  flex-shrink: 0;
  padding: ${(props) => (props.$collapsed ? "0" : "0 10px 0 16px")};
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
`;

const BrandLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  text-decoration: none;
  color: ${(props) => props.theme.textColor};

  img {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
  }
`;

const BrandName = styled.span`
  font-size: 15px;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

const BrandTag = styled.span`
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(props) => props.theme.secondaryTextColor};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 5px;
  padding: 1px 5px;
`;

const iconButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: ${(props) => props.theme.mutedTextColor};
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${(props) => props.theme.surfaceHover};
    color: ${(props) => props.theme.textColor};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const IconButton = styled.button`
  ${iconButton}
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 4px 10px 12px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SectionLabel = styled.div`
  padding: 0 10px;
  margin: 18px 0 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${(props) => props.theme.mutedTextColor};
`;

const SectionRule = styled.div`
  height: 1px;
  margin: 12px 8px;
  background: ${(props) => props.theme.border};
`;

const itemStyles = css<{ $active?: boolean; $collapsed?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 34px;
  margin: 1px 0;
  padding: ${(props) => (props.$collapsed ? "0" : "0 10px")};
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};
  border: 0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  line-height: 1;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  color: ${(props) =>
    props.$active ? props.theme.textColor : props.theme.secondaryTextColor};
  background: ${(props) =>
    props.$active ? props.theme.surfaceActive : "transparent"};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    color: ${(props) => props.theme.textColor};
    background: ${(props) =>
      props.$active ? props.theme.surfaceActive : props.theme.surfaceHover};
  }

  svg.nav-icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: ${(props) =>
      props.$active ? props.theme.colors.primary : "currentColor"};
  }

  /* Accent marker on the active page */
  ${(props) =>
    props.$active &&
    css`
      &::before {
        content: "";
        position: absolute;
        left: -10px;
        top: 8px;
        bottom: 8px;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: ${props.theme.colors.primary};
      }
    `}
`;

const ItemLink = styled(Link)<{ $active?: boolean; $collapsed?: boolean }>`
  ${itemStyles}
`;

const ItemAnchor = styled.a<{ $active?: boolean; $collapsed?: boolean }>`
  ${itemStyles}
`;

const ItemButton = styled.button<{ $active?: boolean; $collapsed?: boolean }>`
  ${itemStyles}
  text-align: left;
`;

const ItemLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Trailing = styled.span<{ $open?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${(props) => props.theme.mutedTextColor};
  font-size: 11.5px;
  font-weight: 500;

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.18s ease;
    transform: rotate(${(props) => (props.$open ? "180deg" : "0deg")});
  }
`;

const Footer = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-direction: ${(props) => (props.$collapsed ? "column" : "row")};
  padding: ${(props) => (props.$collapsed ? "10px 0" : "10px 10px 10px 14px")};
  border-top: 1px solid ${(props) => props.theme.border};
`;

const UserBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${(props) => props.theme.brandGradient};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const UserName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme.textColor};
`;

const readMoreOpen = (): boolean => {
  try {
    return localStorage.getItem(MORE_OPEN_KEY) === "1";
  } catch {
    return false;
  }
};

const MenuItem: React.FC<{
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}> = ({ item, collapsed, onNavigate }) => {
  const location = useLocation();
  const active = isNavItemActive(item, location.pathname);
  const { Icon } = item;

  const content = (
    <>
      <Icon className="nav-icon" />
      {!collapsed && <ItemLabel>{item.title}</ItemLabel>}
      {!collapsed && item.external && (
        <Trailing>
          <ArrowTopRightOnSquareIcon />
        </Trailing>
      )}
    </>
  );

  const element = item.external ? (
    <ItemAnchor
      href={item.path}
      target="_blank"
      rel="noreferrer"
      $collapsed={collapsed}
      aria-label={item.title}
    >
      {content}
    </ItemAnchor>
  ) : (
    <ItemLink
      to={item.path}
      $active={active}
      $collapsed={collapsed}
      aria-current={active ? "page" : undefined}
      aria-label={item.title}
      onClick={onNavigate}
    >
      {content}
    </ItemLink>
  );

  if (!collapsed) return element;

  return (
    <Tooltip title={item.title} placement="right" enterDelay={0}>
      {element}
    </Tooltip>
  );
};

const NavMenu: React.FC<{
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
}> = ({ collapsed = false, onToggleCollapsed, onNavigate }) => {
  const location = useLocation();
  const logout = useLogout();
  const user = useAuthStore.use.user();
  const [moreOpen, setMoreOpen] = useState<boolean>(readMoreOpen);

  const activeInMore = MORE_NAV_ITEMS.some((item) =>
    isNavItemActive(item, location.pathname),
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  // Landing on a page that lives under "More" should reveal where you are.
  useEffect(() => {
    if (activeInMore) setMoreOpen(true);
  }, [activeInMore]);

  // Keep the current page visible when the list is taller than the window.
  useEffect(() => {
    scrollRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView({ block: "nearest" });
  }, [location.pathname, moreOpen]);

  const toggleMore = () => {
    setMoreOpen((open) => {
      const next = !open;
      try {
        localStorage.setItem(MORE_OPEN_KEY, next ? "1" : "0");
      } catch {
        // Storage can be unavailable (private mode), the toggle still works.
      }
      return next;
    });
  };

  const userLabel = user?.email ?? "Admin";

  return (
    <MenuContainer aria-label="Main">
      <Brand $collapsed={collapsed}>
        {!collapsed && (
          <BrandLink to="/" onClick={onNavigate}>
            <img src={`${process.env.PUBLIC_URL}/logo-small.png`} alt="" />
            <BrandName>Minglify</BrandName>
            <BrandTag>Backoffice</BrandTag>
          </BrandLink>
        )}
        {onToggleCollapsed && (
          <Tooltip
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            placement="right"
          >
            <IconButton
              onClick={onToggleCollapsed}
              aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              style={collapsed ? undefined : { marginLeft: "auto" }}
            >
              {collapsed ? <ChevronDoubleRightIcon /> : <ChevronDoubleLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Brand>

      <Scroll ref={scrollRef}>
        {PRIMARY_NAV_SECTIONS.map((section, index) => (
          <div
            key={section.title ?? index}
            style={index === 0 && !section.title ? { marginTop: 4 } : undefined}
          >
            {collapsed
              ? index > 0 && <SectionRule />
              : section.title && (
                  <SectionLabel
                    style={index === 0 ? { marginTop: 8 } : undefined}
                  >
                    {section.title}
                  </SectionLabel>
                )}
            {section.items.map((item) => (
              <MenuItem
                key={item.path}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ))}

        <SectionRule />

        {collapsed ? (
          <Tooltip
            title={moreOpen ? "Hide more pages" : "More pages"}
            placement="right"
            enterDelay={0}
          >
            <ItemButton
              $collapsed
              $active={false}
              onClick={toggleMore}
              aria-expanded={moreOpen}
              aria-label="More pages"
            >
              <EllipsisHorizontalIcon className="nav-icon" />
            </ItemButton>
          </Tooltip>
        ) : (
          <ItemButton onClick={toggleMore} aria-expanded={moreOpen}>
            <EllipsisHorizontalIcon className="nav-icon" />
            <ItemLabel>More</ItemLabel>
            <Trailing $open={moreOpen}>
              {!moreOpen && MORE_NAV_ITEMS.length}
              <ChevronDownIcon />
            </Trailing>
          </ItemButton>
        )}

        {moreOpen &&
          MORE_NAV_ITEMS.map((item) => (
            <MenuItem
              key={item.path}
              item={item}
              collapsed={collapsed}
              onNavigate={onNavigate}
            />
          ))}
      </Scroll>

      <Footer $collapsed={collapsed}>
        <UserBadge>{userLabel.charAt(0)}</UserBadge>
        {!collapsed && (
          <UserInfo>
            <UserName>{userLabel}</UserName>
          </UserInfo>
        )}
        <Tooltip title="Log out" placement={collapsed ? "right" : "top"}>
          <IconButton onClick={logout} aria-label="Log out">
            <ArrowRightOnRectangleIcon />
          </IconButton>
        </Tooltip>
      </Footer>
    </MenuContainer>
  );
};
export default NavMenu;
